/* eslint-disable import/order */
/* eslint-disable camelcase */
import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Card,
  Box,
  Grid,
  Container,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import axios, { getErrorMessage } from 'utils/axios';
import useIsMountedRef from 'hooks/useIsMountedRef';
import { PATH_DASHBOARD } from '../../routes/paths';

import useSettings from '../../hooks/useSettings';
import { useSnackbar } from 'notistack';
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { DataGrid } from '@mui/x-data-grid';
import CustomGridToolbar from 'components/CustomGridToolbar';
import SummaryCard from 'components/SummaryCard';

import Chart from 'react-apexcharts';
import CRUD from 'utils/Crud';

const COLORS = ["#43a047", "#e53935", "#fdd835", "#8e24aa", "#0288d1", "#ffb300"];

export default function PushNotificationCampaignDetails() {
  const { themeStretch } = useSettings();
  const isMountedRef = useIsMountedRef();
  const [data, setData] = useState({});
  const [state, setState] = useState({ loading: false, loadComplete: false, error: null });
  const { id = '' } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getInitData();
    // eslint-disable-next-line
  }, []);

  // Prepare data for ApexCharts

  const statusPieData = [
    { name: "Sent", value: data.summary?.sent || 0 },
    { name: "Failed", value: data.summary?.failed || 0 },
    { name: "Pending", value: data.summary?.pending || 0 },
    { name: "Processing", value: data.summary?.processing || 0 },
  ];

  const workerStats = data.worker_stats || [];
  const barData = workerStats.map(w => ({
    worker: w.worker_id,
    Sent: w.messages_sent,
    Failed: w.messages_failed,
    Retries: w.retry_attempts,
  }));

  const sysTrendData = workerStats.map((w, i) => ({
    worker: w.worker_id,
    mem: w.metrics?.memory_usage_bytes || 0,
    peak: w.metrics?.memory_peak_bytes || 0,
    cpu: Array.isArray(w.metrics?.cpu_load) ? w.metrics.cpu_load[0] : w.metrics?.cpu_load || 0,
    batch_time: w.metrics?.batch_duration_seconds || 0,
    duration: w.duration_seconds || 0,
  }));

  // Error logs for display
  const errorLogs = workerStats.flatMap(w =>
    (w.error_logs ? w.error_logs.split('\n').filter(l => !!l) : [])
  );

  // ApexCharts options

  // 1. Pie chart for notification status
  const pieOptions = {
    labels: statusPieData.map(d => d.name),
    colors: COLORS.slice(0, statusPieData.length),
    legend: { position: 'bottom' },
    tooltip: { y: { formatter: val => val.toString() } },
    responsive: [{
      breakpoint: 480,
      options: { chart: { width: 300 }, legend: { position: 'bottom' } },
    }],
  };
  const pieSeries = statusPieData.map(d => d.value);

  // 2. Bar chart for workers throughput
  const barOptions = {
    chart: { stacked: false, toolbar: { show: true } },
    xaxis: { categories: barData.map(d => d.worker) },
    colors: COLORS.slice(0, 3),
    legend: { position: 'top' },
    tooltip: { y: { formatter: val => val.toString() } },
  };
  const barSeries = [
    { name: 'Sent', data: barData.map(d => d.Sent) },
    { name: 'Failed', data: barData.map(d => d.Failed) },
    { name: 'Retries', data: barData.map(d => d.Retries) },
  ];

  // 3. System metrics area chart
  
  const areaOptions = {
    chart: {
      toolbar: { show: true },
      zoom: { enabled: false }
    },
    xaxis: {
      categories: sysTrendData.map(d => d.worker)
    },
    stroke: { curve: 'smooth' },
    colors: [COLORS[0], COLORS[3], COLORS[1]],
    legend: { position: 'top' },
    tooltip: { shared: true },
    yaxis: [
      {
        title: { text: 'Memory Usage (bytes)' },
        labels: {
          formatter: val => val?.toLocaleString() || '',
        },
        axisBorder: { show: true }
      },
      {
        opposite: true,
        title: { text: 'CPU Load' },
        min: 0,
        max: 5,
        labels: { formatter: val => val?.toFixed(2) || '' },
        axisBorder: { show: true }
      },
    ]
  };
  
  const areaSeries = [
    { name: 'Memory Usage', type: 'area', data: sysTrendData.map(d => d.mem || 0) },
    { name: 'Mem Peak', type: 'area', data: sysTrendData.map(d => d.peak || 0) },
    { name: 'CPU Load', type: 'line', data: sysTrendData.map(d => d.cpu !== undefined ? d.cpu : 0) },
  ];
  

  // 4. Worker durations & batch time line chart
  const lineOptions = {
    chart: { toolbar: { show: true } },
    xaxis: { categories: sysTrendData.map(d => d.worker) },
    stroke: { curve: 'smooth' },
    colors: [COLORS[4], COLORS[5]],
    legend: { position: 'top' },
    tooltip: { shared: true },
  };
  const lineSeries = [
    { name: 'Batch Time (s)', data: sysTrendData.map(d => d.batch_time) },
    { name: 'Worker Duration (s)', data: sysTrendData.map(d => d.duration) },
  ];

  return (
    <Page title="Campaign Details">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Campaign Details - Dashboard"
          links={[
            { name: 'Push Notifications', href: PATH_DASHBOARD.pushNotification.campaign },
            { name: 'Campaigns', href: PATH_DASHBOARD.pushNotification.campaign },
            { name: 'Campaign Details' },
          ]}
        />

        {state.loading && (
          <Box display="flex" justifyContent="center" py={8}><Typography>Loading...</Typography></Box>
        )}

        {!state.loading && state.error != null && (
          <Card>
            <Box padding={10} display="flex" justifyContent="center">
              <Typography color="error">Error: {state.error} </Typography>
            </Box>
          </Card>
        )}

        {!state.loading && state.error == null && (
          <>
            {/* Top Summary Cards */}
            <Box mb={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={2}>
                  <SummaryCard title="Workers" value={data.summary?.num_workers || 0} icon="mdi:worker" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <SummaryCard title="Notifications" value={data.summary?.total_notifications || 0} icon="mdi:bell" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <SummaryCard title="Sent" value={data.summary?.sent || 0} icon="mdi:send-check-outline" color={COLORS[0]} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <SummaryCard title="Failed" value={data.summary?.failed || 0} icon="mdi:close-octagon-outline" color={COLORS[1]} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <SummaryCard title="Pending" value={data.summary?.pending || 0} icon="mdi:clock-outline" color={COLORS[2]} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <SummaryCard title="Processing" value={data.summary?.processing || 0} icon="mdi:tune-vertical" color={COLORS[3]} />
                </Grid>
              </Grid>
            </Box>

            {/* Status Pie and Worker Bar Chart */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2, height: 330 }}>
                  <Typography variant="h6" gutterBottom>Notification Status Distribution</Typography>
                  <Chart options={pieOptions} series={pieSeries} type="pie" width="100%" height="250" />
                </Card>
              </Grid>
              <Grid item xs={12} md={8}>
                <Card sx={{ p: 2, height: 330 }}>
                  <Typography variant="h6" gutterBottom>Worker Throughput</Typography>
                  <Chart options={barOptions} series={barSeries} type="bar" width="100%" height="250" />
                </Card>
              </Grid>
            </Grid>

            {/* System Metrics Trend Graphs */}
            <Grid container spacing={3} mt={1}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, height: 290 }}>
                  <Typography variant="h6" gutterBottom>System Resource Trends</Typography>
                  <Chart options={areaOptions} series={areaSeries} type="area" width="100%" height={200} />
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, height: 290 }}>
                  <Typography variant="h6" gutterBottom>Worker Durations & Batch Timing</Typography>
                  <Chart options={lineOptions} series={lineSeries} type="line" width="100%" height={200} />
                </Card>
              </Grid>
            </Grid>

            {/* Workers Table */}
            <Card sx={{ my: 3 }}>
              <Typography variant="h6" sx={{ px: 2, pt: 2 }}>Worker Sessions</Typography>
              <Box sx={{ height: 380, width: '100%' }}>
                <DataGrid
                  autoHeight
                  rows={workerStats.map((w, i) => ({ ...w, id: i }))}
                  columns={[
                    { field: "worker_id", headerName: "Worker ID", width: 140 },
                    { field: "status", headerName: "Status", width: 120 },
                    { field: "messages_sent", headerName: "Sent", type: "number", width: 90 },
                    { field: "messages_failed", headerName: "Failed", type: "number", width: 90 },
                    { field: "retry_attempts", headerName: "Retries", type: "number", width: 90 },
                    { field: "started_at", headerName: "Started", width: 170 },
                    { field: "ended_at", headerName: "Ended", width: 170 },
                    { field: "duration_seconds", headerName: "Duration (s)", width: 120 },
                  ]}
                  disableSelectionOnClick
                  components={{ Toolbar: CustomGridToolbar }}
                />
              </Box>
            </Card>

            {/* Error Logs */}
            <Card sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ m: 2 }}>Error Logs (All Workers)</Typography>
              <Box sx={{
                maxHeight: 200,
                overflowY: 'auto',
                p: 2,
                background: "#252525", color: "#fafafa", fontFamily: 'monospace', fontSize: 13
              }}>
                {errorLogs.length === 0 && <span>No critical errors or warnings recorded.</span>}
                {errorLogs.map((log, idx) => (
                  <Box key={idx} sx={{ mb: 1 }}>{log}</Box>
                ))}
              </Box>
            </Card>
          </>
        )}
      </Container>
    </Page>
  );

  async function getInitData() {
    setState({ ...state, loading: true, loadComplete: false, error: null });

    try {
      const response = await axios.get(`${CRUD.campaign.root}/${id}/details`);
      if (isMountedRef.current) {
        setData(response.data);
      }
      setState({ ...state, loading: false, loadComplete: true, error: null });
    } catch (error) {
      setState({ ...state, loading: false, loadComplete: false, error: getErrorMessage(error) });
    }
  }
}
